import React, { createContext, useContext, useEffect, useState } from 'react';
import { createApp } from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge/utilities';
import { setAppBridgeInstance } from '../utils/apiClient';

const AppBridgeContext = createContext(null);

export const useAppBridgeInstance = () => {
  return useContext(AppBridgeContext);
};

export const useSessionToken = () => {
  const app = useAppBridgeInstance();
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    if (app) {
      getSessionToken(app)
        .then(token => setSessionToken(token))
        .catch(err => console.error('Failed to get session token:', err));
    }
  }, [app]);

  return sessionToken;
};

/**
 * AppBridgeProvider - Wrapper for Shopify App Bridge
 * 
 * Detects if app is running inside Shopify Admin (embedded) or standalone
 * and provides App Bridge context accordingly
 */
export function AppBridgeProvider({ children }) {
  const [appInstance, setAppInstance] = useState(null);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if app is embedded in Shopify Admin
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    const shop = urlParams.get('shop');

    if (host && shop) {
      // App is embedded in Shopify Admin
      setIsEmbedded(true);
      
      try {
        // Create App Bridge instance
        const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY;
        
        if (!apiKey) {
          console.error('REACT_APP_SHOPIFY_API_KEY is not set');
          throw new Error('Missing Shopify API key');
        }
        
        const config = {
          apiKey: apiKey,
          host: host,
          forceRedirect: true
        };
        
        const app = createApp(config);
        setAppInstance(app);
        // Set instance in apiClient for automatic token injection
        setAppBridgeInstance(app);
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize App Bridge:', error);
        setLoading(false);
      }
    } else {
      // App is running standalone (development/testing)
      setIsEmbedded(false);
      setAppBridgeInstance(null);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppBridgeContext.Provider value={appInstance}>
      {children}
    </AppBridgeContext.Provider>
  );
}
